import * as Cesium from 'cesium';
import { DATA_SOURCES } from '../config/dataSources';

export interface WaterLoadResult {
  dataSource: Cesium.GeoJsonDataSource;
  waterPrimitive?: Cesium.GroundPrimitive;
  tileset?: Cesium.Cesium3DTileset;
  setVisible: (visible: boolean) => void;
}

const WATER_NORMAL_MAP = '/cesiumStatic/Assets/Textures/waterNormalsSmall.jpg';

async function createWaterPrimitive(
  viewer: Cesium.Viewer,
  dataSource: Cesium.GeoJsonDataSource,
): Promise<Cesium.GroundPrimitive | undefined> {

  const time = Cesium.JulianDate.now();
  const geometryInstances: Cesium.GeometryInstance[] = [];
  const vertexFormat =
    Cesium.MaterialAppearance.MaterialSupport.ALL.vertexFormat;

  const polygons = dataSource.entities.values.filter(entity => entity.polygon);
  if (!Cesium.GroundPrimitive.isSupported(viewer.scene) ||
      !Cesium.GroundPrimitive.supportsMaterials(viewer.scene)) return undefined;
  for (const entity of polygons) {
    const hierarchy = entity.polygon?.hierarchy?.getValue(time);
    if (!hierarchy || hierarchy.positions.length < 3) continue;
    geometryInstances.push(
      new Cesium.GeometryInstance({
        geometry: new Cesium.PolygonGeometry({
          polygonHierarchy: hierarchy,
          perPositionHeight: false,
          vertexFormat,
        }),
      }),
    );
  }

  if (geometryInstances.length === 0) return undefined;

  // 水位の実測値がない状態で水平面を置くと、河川地形の下へ隠れる。
  // 更新した水域境界を地形だけに投影し、3D建物を塗らない。
  // Cesium標準Waterは、法線マップのスクロール、波の法線、日光の
  // スペキュラ反射を内部シェーダーで処理する。blendColorは視線角で
  // 明るく見える反射色として設定し、GroundPrimitiveでも描画できる。
  const waterMaterial = Cesium.Material.fromType(Cesium.Material.WaterType, {
    baseWaterColor: Cesium.Color.fromBytes(26, 92, 102, 222),
    blendColor: Cesium.Color.fromBytes(92, 178, 185, 148),
    normalMap: WATER_NORMAL_MAP,
    // Cesium WaterはspecularMapの赤チャンネルを水面マスクにも使うため、
    // 法線画像を指定して波の凹凸と反射領域を同時に出す。
    specularMap: WATER_NORMAL_MAP,
    frequency: 18.0,
    animationSpeed: 0.035,
    amplitude: 1.8,
    specularIntensity: 1.15,
    fadeFactor: 0.4,
  });
  // Cesium標準Waterの波処理は維持しつつ、視線角が浅いほど空の色を
  // 混ぜるフレネル反射を追加する。Cesium 1.145の標準Waterソースに対する
  // 差分なので、法線マップ・標準の照明計算・波アニメーションを失わない。
  const normalAnchor = 'material.normal = normalize(materialInput.tangentToEyeMatrix * normalTangentSpace);';
  if (waterMaterial.shaderSource.includes(normalAnchor)) {
    waterMaterial.shaderSource = waterMaterial.shaderSource.replace(
      normalAnchor,
      `${normalAnchor}
    vec3 waterViewDirection = normalize(materialInput.positionToEyeEC);
    float fresnel = 0.03 + 0.97 * pow(1.0 - clamp(abs(dot(material.normal, waterViewDirection)), 0.0, 1.0), 5.0);
    material.diffuse = mix(material.diffuse, vec3(0.36, 0.70, 0.73), fresnel * 0.38);`,
    );
  } else {
    console.warn('[Water] 標準Waterのフレネル拡張位置を検出できませんでした');
  }

  return new Cesium.GroundPrimitive({
    geometryInstances,
    appearance: new Cesium.MaterialAppearance({
      material: waterMaterial,
      materialSupport: Cesium.MaterialAppearance.MaterialSupport.ALL,
      faceForward: true,
      translucent: true,
      closed: false,
    }),
    classificationType: Cesium.ClassificationType.TERRAIN,
    allowPicking: false,
    asynchronous: true,
    releaseGeometryInstances: true,
  });
}

export async function loadWaterSurface(
  viewer: Cesium.Viewer,
  onError: (error: unknown) => void,
): Promise<WaterLoadResult> {
  try {
    // 徳島市で実際に水域を描画するための軽量なフォールバック。
    // clampToGroundにより、地形の上へ水域ポリゴンを重ねる。
    const dataSource = await Cesium.GeoJsonDataSource.load(
      DATA_SOURCES.waterGeoJson,
      {
        clampToGround: true,
        stroke: Cesium.Color.fromBytes(26, 92, 102, 206),
        strokeWidth: 1,
        fill: Cesium.Color.fromBytes(26, 92, 102, 206),
      },
    );
    viewer.dataSources.add(dataSource);

    // 法線画像は色画像ではないため、そのまま水面の模様として描画しない。
    const fallbackWaterMaterial = new Cesium.ColorMaterialProperty(
      Cesium.Color.fromBytes(26, 92, 102, 206),
    );

    for (const entity of dataSource.entities.values) {
      if (!entity.polygon) continue;
      // Waterプリミティブ非対応端末でも法線画像を目視できるよう、
      // 同じテクスチャを薄いフォールバックとして重ねる。
      entity.polygon.material = fallbackWaterMaterial;
      entity.polygon.classificationType = new Cesium.ConstantProperty(
        Cesium.ClassificationType.TERRAIN,
      );
      entity.polygon.outline = new Cesium.ConstantProperty(false);
      entity.polygon.outlineColor = new Cesium.ConstantProperty(
        Cesium.Color.fromBytes(87, 220, 225, 220),
      );
    }

    // GeoJSONの単色塗りを初期表示・非対応端末用に残し、準備完了後は
    // 法線マップと日光の光沢を使うWaterマテリアルへ切り替える。
    const waterPrimitive = await createWaterPrimitive(viewer, dataSource);
    if (waterPrimitive) {
      viewer.scene.primitives.add(waterPrimitive);
      // Waterが準備できたら補助ポリゴンを隠し、二重塗りを防ぐ。
      const removeReadyListener = viewer.scene.postRender.addEventListener(() => {
        if (waterPrimitive.ready) {
          dataSource.show = false;
          removeReadyListener();
          console.info('[Water] Waterマテリアル準備完了。フォールバックを非表示にしました');
          viewer.scene.requestRender();
        }
      });
      window.setTimeout(() => {
        if (!waterPrimitive.ready) {
          console.warn('[Water] Waterマテリアルの準備に時間がかかっています。フォールバックを表示中です');
        }
      }, 20000);
      console.info('[Water] Waterマテリアルプリミティブを追加しました');
    } else {
      console.info('[Water] 法線画像フォールバックで表示します');
    }

    let tileset: Cesium.Cesium3DTileset | undefined;
    if (DATA_SOURCES.water) {
      tileset = await Cesium.Cesium3DTileset.fromUrl(DATA_SOURCES.water, {
        scene: viewer.scene,
        maximumScreenSpaceError: 24,
        cacheBytes: 96 * 1024 * 1024,
        maximumCacheOverflowBytes: 48 * 1024 * 1024,
        dynamicScreenSpaceError: true,
        dynamicScreenSpaceErrorDensity: 2.0e-4,
        dynamicScreenSpaceErrorFactor: 20,
        foveatedScreenSpaceError: true,
        foveatedTimeDelay: 0.15,
        shadows: Cesium.ShadowMode.DISABLED,
        enableCollision: false,
      });

      tileset.style = new Cesium.Cesium3DTileStyle({
        color: 'color("#3c9fb3", 0.62)',
      });
      tileset.shadows = Cesium.ShadowMode.DISABLED;
      viewer.scene.primitives.add(tileset);
    }

    let animationTimer: number | undefined;
    const setAnimationEnabled = (enabled: boolean): void => {
      if (animationTimer !== undefined) {
        window.clearInterval(animationTimer);
        animationTimer = undefined;
      }
      // requestRenderModeでは画面が静止するとフレーム番号も進まないため、
      // 水面だけ8fpsで再描画して波と光沢のアニメーションを維持する。
      if (enabled && waterPrimitive) {
        animationTimer = window.setInterval(() => {
          if (!viewer.isDestroyed()) {
            const appearance = waterPrimitive.appearance as Cesium.MaterialAppearance;
            // Waterシェーダーはczm_frameNumberを時間として使うため、
            // requestRenderを125ms間隔で発行して静止中も8fpsを維持する。
            appearance.material.uniforms.animationSpeed = 0.035;
            viewer.scene.requestRender();
          }
        }, 125);
      }
    };

    const setVisible = (visible: boolean): void => {
      dataSource.show = visible && !waterPrimitive?.ready;
      if (waterPrimitive) waterPrimitive.show = visible;
      if (tileset) tileset.show = visible;
      setAnimationEnabled(visible);
      viewer.scene.requestRender();
    };
    setVisible(true);
    return { dataSource, waterPrimitive, tileset, setVisible };
  } catch (error) {
    onError(error);
    throw error;
  }
}
